import {GridModel} from '@xh/hoist/cmp/grid';
import {HoistModel, managed, TaskObserver, XH} from '@xh/hoist/core';
import {actionCol, calcActionColWidth} from '@xh/hoist/desktop/cmp/grid';
import {fmtDateTimeSec} from '@xh/hoist/format';
import {Icon} from '@xh/hoist/icon';
import {observable, runInAction} from '@xh/hoist/mobx';
import {Workflow, WorkflowExecution} from '../../data/DataTypes';
import {cloneDeep} from 'lodash';
import {WorkflowEditorModel} from './WorkflowEditorModel';
import {asServerException} from '../../../core/ServiceUtils';
import {spinner} from '@xh/hoist/cmp/spinner';
import {WorkflowLogModel} from './WorkflowLogModel';
import cronstrue from 'cronstrue';
import {DataCheckDialogModel} from './DataCheckDialogModel';
import {NotificationMessage} from '../../data/MessageTypes';
import {datesToSpan} from '../../data/Utils';

export class WorkflwosTabModel extends HoistModel {
    workflowsTask = TaskObserver.trackLast();
    @observable accessor showLog = false;
    @managed workflowsGrid = this.createWorkflowsGridModel();
    @managed executionsGrid = this.createExecutionsGridModel();
    @managed workflowEditor = new WorkflowEditorModel();
    @managed workflowLog = new WorkflowLogModel();
    @managed dataCheckDialog = new DataCheckDialogModel();

    constructor() {
        super();

        XH.messageHub.subscribe('workflow_execution', this.xhId, message => this.onMessage(message));

        this.addReaction({
            track: () => XH.workflowService.workflows,
            run: workflows => this.workflowsGrid.loadData(workflows),
            fireImmediately: true
        });

        this.addReaction({
            track: () => XH.workflowService.executions,
            run: executions => this.executionsGrid.loadData(Array.from(executions.values())),
            fireImmediately: true
        });

        this.addReaction({
            track: () => this.workflowsGrid.selectedId,
            run: id => this.filterExecutions(id)
        });
    }

    async refreshAllAsync() {
        await XH.workflowService.refreshAsync().linkTo(this.workflowsTask);
    }

    get selectedWorkflow(): Workflow {
        return this.workflowsGrid.selectedRecord?.raw as Workflow;
    }

    async runWorkflow(id: number) {
        try {
            await XH.workflowService.runAsync(id).linkTo(this.workflowsTask);
        } catch (e) {
            XH.handleException(asServerException(e), {title: 'Error running workflow'});
        }
    }

    async killExecution(id: number) {
        try {
            await XH.workflowService.killAsync(id).linkTo(this.workflowsTask);
        } catch (e) {
            XH.handleException(asServerException(e), {title: 'Error stopping workflow'});
        }
    }

    addWorkflow() {
        this.workflowEditor.open(null);
    }

    cloneWorkflow() {
        const copy = cloneDeep(this.selectedWorkflow);
        copy.id = 0;
        copy.name = `${copy.name} - COPY`;
        XH.workflowService.saveWorkflowAsync(copy).linkTo(this.workflowsTask);
    }

    editWorkflow() {
        this.workflowEditor.open(this.selectedWorkflow);
    }

    deleteWorkflow() {
        XH.confirm({
            icon: Icon.warning(),
            title: 'User confirmation required',
            message: 'Please confirm deletion of the workflow'
        }).then(result => {
            if (result) XH.workflowService.deleteWorkflowAsync(this.selectedWorkflow.id).linkTo(this.workflowsTask);
        });
    }

    openDataChecks() {
        this.dataCheckDialog.open();
    }

    onMessage(message: NotificationMessage) {
        switch (message.envelope.eventType) {
            case 'RELOAD':
                this.refreshAllAsync();
                break;
            default:
                this.onExecutionUpdate(message.payload);
                break;
        }
    }

    private onExecutionUpdate(workflowExecution: WorkflowExecution) {
        this.executionsGrid.updateData([workflowExecution]);
    }

    private filterExecutions(workflowId) {
        this.executionsGrid.store.setFilter({field: 'workflowId', op: '=', value: workflowId});
    }

    private viewLog(id: number) {
        this.workflowLog.open(id);
    }

    private createWorkflowsGridModel(): GridModel {
        return new GridModel({
            persistWith: {prefKey: 'workflowsTabState', path: 'workflowsGrid'},
            onRowDoubleClicked: () => this.editWorkflow(),
            colChooserModel: true,
            contextMenu: null,
            columns: [
                {field: 'id'},
                {field: 'name'},
                {field: 'command'},
                {
                    field: 'schedule',
                    tooltip: v => cronstrue.toString(v)
                },
                {
                    field: 'isEnabled',
                    headerName: null,
                    width: 35,
                    resizable: false,
                    renderer: v => (v ? Icon.bolt({intent: 'warning'}) : null)
                },
                {field: 'updated', renderer: v => fmtDateTimeSec(v)},
                {field: 'updatedBy'},
                {
                    ...actionCol,
                    width: calcActionColWidth(1),
                    actions: [
                        {
                            icon: Icon.playCircle(),
                            intent: 'success',
                            actionFn: ({record}) => this.runWorkflow(record.data.id)
                        }
                    ]
                }
            ]
        });
    }

    private createExecutionsGridModel(): GridModel {
        return new GridModel({
            persistWith: {prefKey: 'workflowsTabState', path: 'executionsGrid'},
            colChooserModel: true,
            sortBy: 'startTime|desc',
            onRowClicked: () => runInAction(() => (this.showLog = true)),
            store: {
                processRawData: (we: WorkflowExecution) => {
                    return {
                        ...we,
                        workflowName: XH.workflowService.workflowMap.get(we.workflowId)?.name
                    };
                }
            },
            columns: [
                {field: 'id'},
                {field: 'workflowId', hidden: true},
                {field: 'workflowName', flex: 1},
                {field: 'completed', hidden: true},
                {
                    field: 'status',
                    renderer: v =>
                        v === 'RUNNING'
                            ? spinner({compact: true})
                            : v === 'OK'
                              ? Icon.check({intent: 'success'})
                              : v === 'KILLED'
                                ? Icon.skull({intent: 'danger'})
                                : Icon.x({intent: 'danger'})
                },
                {field: 'startTime', renderer: v => fmtDateTimeSec(v)},
                {
                    field: 'endTime',
                    renderer: (v: string) => (v.startsWith('000') ? null : fmtDateTimeSec(v))
                },
                {field: 'updated', renderer: v => fmtDateTimeSec(v)},
                {
                    field: 'elapsed',
                    align: 'right',
                    rendererIsComplex: true,
                    renderer: (v, {record}) => {
                        if (!record.data.endTime) return;

                        const start = new Date(record.data.startTime),
                            end = new Date(record.data.endTime);
                        return datesToSpan(start, end);
                    }
                },
                {field: 'updatedBy', headerName: 'user'},
                {
                    ...actionCol,
                    width: calcActionColWidth(2),
                    actions: [
                        {
                            icon: Icon.x(),
                            intent: 'danger',
                            displayFn: ({record}) => {
                                return {hidden: record.data.completed};
                            },
                            actionFn: ({record}) => this.killExecution(record.data.id)
                        },
                        {
                            icon: Icon.eye(),
                            intent: 'primary',
                            actionFn: ({record}) => this.viewLog(record.data.id)
                        }
                    ]
                },
                {field: 'errorMessage', width: 200, tooltip: v => v}
            ]
        });
    }
}
